// eslint-disable-next-line import/no-extraneous-dependencies
import { JSEncrypt } from "jsencrypt";
import { useState, useEffect, useRef } from "react";
import useCurrentResourceCentre from "./useCurrentResourceCentre";
import { useCurrentCounter } from "./useCurrentCounter";

export type PaymentStatus = "ENTR" | "PARSED" | "COMPLETED" | "FAILED" | null;
export type WebSocketStatus = "DISCONNECTED" | "CONNECTING" | "CONNECTED" | "ERROR";

interface PropType {
  validationTraceId: string | null;
  onStatusUpdate?: (status: PaymentStatus) => void;
}

interface ReturnType {
  connectionStatus: WebSocketStatus;
  paymentStatus: PaymentStatus;
  start: () => void;
  disconnect: () => void;
  transactionId: number | null;
}

// Encrypt API key (RSA/ECB/PKCS1Padding)
const encryptApiKey = ({ apiKey, publicKey }: { apiKey: string; publicKey: string }): string => {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);

  const encrypted = encrypt.encrypt(apiKey);
  if (!encrypted) throw new Error("Encryption failed");

  return encrypted;
};

export default function useNepalPayWSS({
  validationTraceId,
  onStatusUpdate
}: PropType): ReturnType | null {
  const rc = useCurrentResourceCentre();
  const { currentCounter: terminalLabel } = useCurrentCounter();

  // ref
  const wsRef = useRef<WebSocket | null>(null);

  const apiKey = rc?.dynamicQrPaymentInfo?.nepalPay?.apiKey;
  const publicKey = rc?.dynamicQrPaymentInfo?.nepalPay?.publicKey;

  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(null);
  const [connectionStatus, setConnectionStatus] = useState<WebSocketStatus>("DISCONNECTED");

  const WS_BASE_URL =
    process.env.NODE_ENV === "development"
      ? new URL("wss://devws.nepalpay.com.np")
      : new URL("wss://ws.nepalpay.com.np");

  const WS_SERVER_URL = new URL("/nqrws", WS_BASE_URL);
  const WS_SUBSCRIBE_URL = new URL("/user/nqrws/check-txn-status", WS_BASE_URL);
  const WS_SEND_URL = new URL("/nqrws/check-txn-status", WS_BASE_URL);

  // Send raw STOMP frame
  const sendFrame = (frame: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(frame);
    }
  };

  // Step 1: CONNECT
  const connectWs = () => {
    const stompMessage = "CONNECT\naccept-version:1.1,1.0\nheart-beat:10000,10000\n\n\0";
    sendFrame(stompMessage);
  };

  // Step 2: SUBSCRIBE
  const subscribe = () => {
    const stompMessage = `SUBSCRIBE\nid:sub-0\ndestination:${WS_SUBSCRIBE_URL.pathname}\n\n\0`;
    sendFrame(stompMessage);
  };

  // Step 3: SEND request
  const sendInitialRequest = () => {
    if (!apiKey || !publicKey) {
      return;
    }

    const payload = JSON.stringify({
      merchant_id: terminalLabel,
      request_id: validationTraceId,
      username: rc?.dynamicQrPaymentInfo?.nepalPay?.userName,
      api_token: encryptApiKey({ apiKey, publicKey })
    });

    const { length } = new TextEncoder().encode(payload);
    const stompMessage = `SEND\ndestination:${WS_SEND_URL.pathname}\ncontent-length:${length}\n\n${payload}\0`;
    sendFrame(stompMessage);
  };

  // wss connect
  const start = () => {
    setConnectionStatus("CONNECTING");
    try {
      const ws = new WebSocket(WS_SERVER_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionStatus("CONNECTED");
        connectWs();
        subscribe();
        sendInitialRequest();
      };

      ws.onclose = () => {
        setConnectionStatus("DISCONNECTED");
      };

      ws.onerror = () => {
        setConnectionStatus("ERROR");
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        const parts = event.data.split("\n\n");
        const bodyPart = parts[1]?.replace(/\0$/, "");

        if (bodyPart && bodyPart.trim().startsWith("{")) {
          const parsed = JSON.parse(bodyPart);
          setPaymentStatus(parsed?.status as PaymentStatus);
          setTransactionId(parsed?.txn_id || "");
          onStatusUpdate?.(parsed?.status as PaymentStatus);
        }
      };
    } catch (err) {
      setConnectionStatus("ERROR");
    }
  };

  // diconnect
  const disconnect = () => {
    wsRef.current?.close();
    wsRef.current = null;
    setConnectionStatus("DISCONNECTED");
  };

  useEffect(() => {
    if (validationTraceId) {
      start();
    }

    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationTraceId]);

  if (!apiKey || !publicKey) return null;

  return {
    connectionStatus,
    paymentStatus,
    start,
    disconnect,
    transactionId
  };
}
